import {
    Button,
    CardBody,
    Collapse,
    Container,
    Nav,
    Navbar,
    NavbarBrand,
    NavbarToggler,
    NavItem,
    NavLink
} from "reactstrap";
import {FOOTER_CONTACTS, FOOTER_LINK_GROUPS, FOOTER_SOCIAL_LINKS, PUBLIC_APP_NAME, PUBLIC_NAV_LINKS} from "./utils";
import React, {useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";

export const PublicHeader = () => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const toggle = () => setIsOpen(!isOpen);

    const isActiveLink = (href: string) => {
        const hashIndex = href.indexOf("#");
        if (hashIndex >= 0) {
            const path = href.slice(0, hashIndex) || "/";
            const hash = href.slice(hashIndex);
            return location.pathname === path && location.hash === hash;
        }
        if (href === "/") return location.pathname === "/" && !location.hash;
        return location.pathname.startsWith(href);
    };

    return (
        <header className="oec-header">
            <Navbar light expand="lg" className="oec-header-bar">
                <Container fluid className="oec-header-inner">
                    <NavbarBrand tag={Link} to="/" className="oec-header-logo">
                        <img
                            src="/public/logos/on energy connect logo.png"
                            alt="On Energy Connect"
                        />
                    </NavbarBrand>

                    <div className="oec-header-tools d-lg-none">
                        <button className="oec-header-search" type="button" aria-label="Search">
                            <i className="fas fa-search"/>
                        </button>
                        <NavbarToggler onClick={toggle} className="oec-header-toggler"/>
                    </div>

                    <Collapse isOpen={isOpen} navbar>
                        <Nav className="oec-header-nav" navbar>
                            {PUBLIC_NAV_LINKS.map((link) => {
                                const active = isActiveLink(link.href);
                                return (
                                    <NavItem key={link.href}>
                                        <NavLink
                                            tag={Link}
                                            to={link.href}
                                            className={`oec-nav-link${active ? " is-active" : ""}`}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {link.name}
                                        </NavLink>
                                    </NavItem>
                                );
                            })}
                        </Nav>

                        <div className="oec-header-actions">
                            <button className="oec-header-search d-none d-lg-inline-flex" type="button" aria-label="Search">
                                <i className="fas fa-search"/>
                            </button>
                            <button className="btn-oec-secondary" type="button" onClick={() => navigate("/auth/login")}>
                                Login
                            </button>
                            <button className="btn-oec-primary" type="button" onClick={() => navigate("/auth/signup")}>
                                Create Profile
                            </button>
                        </div>
                    </Collapse>
                </Container>
            </Navbar>
        </header>
    );
};


export const PublicFooter = () => {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="oec-footer">
            <img className="oec-footer-bg" src="/public/sections/footer_landscape.jpg" alt="" loading="lazy"/>

            <div className="oec-footer-inner">
                <div className="oec-footer-grid">
                    <div className="oec-footer-brand">
                        <img src="/public/logos/on energy connect logo.png" alt={PUBLIC_APP_NAME}/>
                        <p>Connect. Discover. Grow.</p>
                    </div>

                    {FOOTER_LINK_GROUPS.map((group) => (
                        <div key={group.title} className="oec-footer-col">
                            <h3>{group.title}</h3>
                            <ul>
                                {group.links.map((link) => (
                                    <li key={link.name}>
                                        <a href={link.href}>{link.name}</a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="oec-footer-col">
                        <h3>Contact Us</h3>
                        <ul>
                            {FOOTER_CONTACTS.map((contact) => (
                                <li key={contact.value}>
                                    <a href={contact.href}>
                                        <i className={contact.icon}/>
                                        <span>{contact.value}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="oec-footer-col">
                        <h3>Follow Us</h3>
                        <div className="oec-footer-social">
                            {FOOTER_SOCIAL_LINKS.map((social) => (
                                <a
                                    key={social.name}
                                    href={social.href}
                                    aria-label={social.name}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <i className={social.icon}/>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="oec-footer-bottom">
                    <p>&copy; {currentYear} On Energy Connect. All rights reserved.</p>
                    <p>
                        <a href="/privacy">Privacy Policy</a>
                        <span>|</span>
                        <a href="/privacy">Terms of Service</a>
                    </p>
                </div>
            </div>
        </footer>
    );
};



/** Section title + optional subtitle, with a "View All …" link on the right. */
export const SectionHeader = (props: {
    title: string;
    subtitle?: string;
    linkLabel: string;
    linkTo: string;
}) => (
    <div className="oec-section-header">
        <div>
            <h2 className="oec-section-title">{props.title}</h2>
            {props.subtitle && <p className="oec-section-subtitle">{props.subtitle}</p>}
        </div>
        <Link to={props.linkTo} className="oec-section-link">
            {props.linkLabel} <i className="fas fa-arrow-right"/>
        </Link>
    </div>
);

export const GradientImageOverlay = ({imagePath}: { imagePath: string }) => {
    return <>
        <img
            src={imagePath}
            alt=""
            className="position-absolute w-100 h-100"
            style={{
                top: 0,
                left: 0,
                objectFit: 'cover',
                objectPosition: 'center',
                zIndex: 1
            }}
        />

        {/* 2. The Gradient Overlay Layer */}
        <div
            className="position-absolute w-100 h-100"
            style={{
                top: 0,
                left: 0,
                background: `linear-gradient(
            180deg, 
            rgba(6, 92, 83, 1) 100%,      /* Brand Color #065C53 - Solid at top */
            rgba(6, 92, 83, 0.60) 60%,    /* Slight transparency in middle */
            rgba(10, 33, 77, 0.80) 70%  /* Fading into a dark blue/green at the bottom */
        )`,
                zIndex: 2, // Higher than the image
                opacity: 0.94 // Adjust this to show more/less of the photo
            }}
        />
    </>

}


export const CurrencyValue = ({value}: { value: string }) => {
    if (!value) return "Unspecified";

    const formatted = new Intl.NumberFormat('am', {
        style: 'currency',
        currency: "ETB",
        minimumFractionDigits: 0
    }).format(parseInt(value));

    return <span className="font-mono">{formatted}</span>;
};

export const DateValue = ({value}: { value?: string }) => {
    if(!value) return "Unknown";

    const ethiopianDateFormatter = new Intl.DateTimeFormat("am-ET-u-ca-ethiopic", {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(new Date(value));

    return (
        <span>{ethiopianDateFormatter}</span>
    );
};


export const MiniInfoCard = (props: {title: string, subtitle: string, action: {label: string, destination: string}}) => {
    const {
        title,
        subtitle,
        action
    } = props;
    const navigate = useNavigate();

    return <div className="border-0 shadow-lg mt-4 text-center rounded-lg" style={{ background: "linear-gradient(rgb(6, 92, 83) 0%, rgb(26, 122, 110) 60%, rgb(42, 143, 130) 100%)" }}>
        <CardBody className="p-4">
            <h5 className="text-white font-weight-bold mb-3">{title}</h5>
            <p className="text-white opacity-8 text-sm mb-4">
                {subtitle}
            </p>
            <Button color="warning" size="sm" className="rounded-pill px-4 text-white font-weight-bold shadow-sm" onClick={() => navigate(action.destination)}>
                {action.label}
            </Button>
        </CardBody>
    </div>
}